import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'light' | 'dark' | 'system'

export interface ColorPreset {
  id: string
  label: string
  primary: string
  secondary: string
}

/** Default brand colours — the Unnati blue and its "mild" hover tone. */
export const DEFAULT_PRIMARY = '#2a85ff'
export const DEFAULT_SECONDARY = '#4996ff'

export const COLOR_PRESETS: ColorPreset[] = [
  { id: 'blue',   label: 'Blue',   primary: DEFAULT_PRIMARY, secondary: DEFAULT_SECONDARY },
  { id: 'violet', label: 'Violet', primary: '#7c3aed', secondary: '#8b5cf6' },
  { id: 'rose',   label: 'Rose',   primary: '#e11d48', secondary: '#f43f5e' },
  { id: 'orange', label: 'Orange', primary: '#ea580c', secondary: '#f97316' },
  { id: 'green',  label: 'Green',  primary: '#16a34a', secondary: '#22c55e' },
  { id: 'amber',  label: 'Amber',  primary: '#d97706', secondary: '#f59e0b' },
]

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return { h: 0, s: 0, l: 0 }

  let r = parseInt(result[1], 16) / 255
  let g = parseInt(result[2], 16) / 255
  let b = parseInt(result[3], 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  }
}

function hslString(hex: string): string {
  const { h, s, l } = hexToHSL(hex)
  return `${h} ${s}% ${l}%`
}

function lightenHSL(hex: string, amount: number): string {
  const { h, s, l } = hexToHSL(hex)
  return `${h} ${s}% ${Math.min(100, l + amount)}%`
}

function darkenHSL(hex: string, amount: number): string {
  const { h, s, l } = hexToHSL(hex)
  return `${h} ${s}% ${Math.max(0, l - amount)}%`
}

export function applyColorsToDOM(primary: string, secondary: string, isDark: boolean) {
  const root = document.documentElement
  const p = hslString(primary)
  const s = hslString(secondary)

  root.style.setProperty('--primary', p)
  root.style.setProperty('--ring', p)
  root.style.setProperty('--chart-1', p)

  // Primary family: "mild" is the hover tone, "deep" the pressed tone.
  root.style.setProperty('--primary-mild', s)
  root.style.setProperty('--primary-deep', darkenHSL(primary, 10))
  root.style.setProperty('--lime', p)

  root.style.setProperty('--chart-2', s)

  // Solid buttons carry white text up to a fairly light primary (the default
  // blue sits at L=58); only genuinely pale colours flip to dark text.
  const pHsl = hexToHSL(primary)
  const needsLightFg = pHsl.l < 65
  root.style.setProperty('--primary-foreground', needsLightFg ? '0 0% 100%' : '0 0% 3.9%')

  root.style.setProperty('--sidebar-primary', isDark ? lightenHSL(primary, 8) : p)
  root.style.setProperty('--sidebar-ring', isDark ? lightenHSL(primary, 8) : p)
}

interface ThemeState {
  theme: Theme
  presetId: string
  primaryColor: string
  secondaryColor: string
  sidebarCollapsed: boolean

  setTheme: (theme: Theme) => void
  applyPreset: (presetId: string) => void
  setCustomColors: (primary: string, secondary: string) => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      presetId: 'blue',
      primaryColor: DEFAULT_PRIMARY,
      secondaryColor: DEFAULT_SECONDARY,
      sidebarCollapsed: false,

      setTheme: (theme) => {
        const root = document.documentElement
        root.classList.remove('light', 'dark')

        const resolved =
          theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : theme
        root.classList.add(resolved)

        const state = get()
        applyColorsToDOM(state.primaryColor, state.secondaryColor, resolved === 'dark')
        set({ theme })
      },

      applyPreset: (presetId) => {
        const preset = COLOR_PRESETS.find((p) => p.id === presetId)
        if (!preset) return

        const { theme } = get()
        const resolved =
          theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : theme

        applyColorsToDOM(preset.primary, preset.secondary, resolved === 'dark')
        set({
          presetId,
          primaryColor: preset.primary,
          secondaryColor: preset.secondary,
        })
      },

      setCustomColors: (primary, secondary) => {
        const { theme } = get()
        const resolved =
          theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : theme

        applyColorsToDOM(primary, secondary, resolved === 'dark')
        set({
          presetId: 'custom',
          primaryColor: primary,
          secondaryColor: secondary,
        })
      },

      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
    }),
    {
      name: 'theme-store',
      version: 3,
      // v3 fixed the brand colours: anything persisted by the old colour
      // picker is reset so every browser lands on the same theme. The
      // light/dark choice and the sidebar state are kept.
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Partial<ThemeState>
        if (version < 3) {
          return {
            ...state,
            presetId: 'blue',
            primaryColor: DEFAULT_PRIMARY,
            secondaryColor: DEFAULT_SECONDARY,
          }
        }
        return state
      },
    },
  ),
)

export const PALETTES = COLOR_PRESETS.map((p) => ({
  value: p.id,
  label: p.label,
  swatches: [p.primary, p.secondary] as [string, string],
}))
